import { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import Link from 'next/link';
import { useDashboardParams, Tabs, getDashboardTabRoute, useFeatureFlag, Flag } from '@algoan/dashboard/utils';
import { useActivityPageAccessControl } from '../../hooks/use-activity-page-access-control';
import {
  AnalysisIcon,
  HomeIcon,
  StatementsIcon,
  DocumentsIcon,
  FinancialDataIcon,
  IdentityIcon,
  HistoryIcon,
  SettingsIcon,
  LoanDecisionIcon,
} from '../icons';
import { sidebarMessages } from './sidebar-messages';
import {
  accessTokenOrganizationIdSelector,
  previousCustomerIdSelector,
  projectIdSelector,
} from '@algoan/dashboard/store/selectors';
import { useSelector } from '@algoan/dashboard/store/store';

const TopContainer: React.FC<{ selectedTab: string }> = ({ selectedTab }) => {
  const { customerId } = useDashboardParams();
  const organizationId = useSelector(accessTokenOrganizationIdSelector) as string;
  const projectId = useSelector(projectIdSelector) as string;
  const previousCustomerId = useSelector(previousCustomerIdSelector);
  const showHistoryButton = useActivityPageAccessControl();
  const intl = useIntl();
  const { active: isStatementsTabHidden } = useFeatureFlag(Flag.HIDE_STATEMENTS_TAB);
  const { active: areTransactionHidden } = useFeatureFlag(Flag.HIDE_THE_TRANSACTIONS);

  if (
    selectedTab === Tabs.HOME ||
    selectedTab === Tabs.HISTORY ||
    (selectedTab === Tabs.SETTINGS && !previousCustomerId)
  ) {
    return (
      <S.TopContainer>
        <Link href="/" scroll={false} passHref={true}>
          <S.Tab selected={selectedTab === Tabs.HOME} expandOnHover>
            <S.HomeIcon />
            <S.TooltipSeparator id="tabSeparator" />
            <S.TabName id="tabName">{intl.formatMessage(sidebarMessages.HOME)}</S.TabName>
          </S.Tab>
        </Link>
        {showHistoryButton && (
          <>
            <S.Separator />
            <Link href="/activity" scroll={false} passHref={true}>
              <S.Tab selected={selectedTab === Tabs.HISTORY} expandOnHover>
                <S.HistoryIconContainer>
                  <HistoryIcon />
                </S.HistoryIconContainer>
                <S.TooltipSeparator id="tabSeparator" />
                <S.TabName data-testid="activity-page" id="tabName">
                  {intl.formatMessage(sidebarMessages.ACTIVITY)}
                </S.TabName>
              </S.Tab>
            </Link>
            <S.Separator />
          </>
        )}
      </S.TopContainer>
    );
  }

  return (
    <S.TopContainer>
      <Link href="/" scroll={false} passHref={true}>
        <S.ReturnToHome data-testid="return-home-tab">
          <S.HomeContainer>
            <S.Tab expandOnHover>
              <S.HomeIcon />
              <S.TooltipSeparator id="tabSeparator" />
              <S.TabName id="tabName">{intl.formatMessage(sidebarMessages.HOME)}</S.TabName>
            </S.Tab>
          </S.HomeContainer>
        </S.ReturnToHome>
      </Link>
      <Link
        href={getDashboardTabRoute(
          organizationId,
          projectId,
          selectedTab === Tabs.SETTINGS && previousCustomerId ? previousCustomerId : customerId,
          Tabs.DECISION,
        )}
        scroll={false}
        passHref={true}
      >
        <S.Tab data-testid="decision-tab" selected={selectedTab === Tabs.DECISION} expandOnHover>
          <S.LoanDecisionIcon />
          <S.TooltipSeparator id="tabSeparator" />
          <S.TabName id="tabName">{intl.formatMessage(sidebarMessages.DECISION)}</S.TabName>
        </S.Tab>
      </Link>
      <S.Separator />
      <Link
        href={getDashboardTabRoute(
          organizationId,
          projectId,
          selectedTab === Tabs.SETTINGS && previousCustomerId ? previousCustomerId : customerId,
          Tabs.ANALYSIS,
        )}
        scroll={false}
        passHref={true}
      >
        <S.Tab data-testid="analysis-tab" selected={selectedTab === Tabs.ANALYSIS} expandOnHover>
          <S.AnalysisIcon />
          <S.TooltipSeparator id="tabSeparator" />
          <S.TabName id="tabName">{intl.formatMessage(sidebarMessages.ANALYSIS)}</S.TabName>
        </S.Tab>
      </Link>

      <S.Separator />
      {!(isStatementsTabHidden || areTransactionHidden) && (
        <>
          <Link
            href={getDashboardTabRoute(
              organizationId,
              projectId,
              selectedTab === Tabs.SETTINGS && previousCustomerId ? previousCustomerId : customerId,
              Tabs.STATEMENTS,
            )}
            scroll={false}
            passHref={true}
          >
            <S.Tab data-testid="statements-tab" selected={selectedTab === Tabs.STATEMENTS} expandOnHover>
              <S.StatementsIcon />
              <S.TooltipSeparator id="tabSeparator" />
              <S.TabName id="tabName">{intl.formatMessage(sidebarMessages.STATEMENTS)}</S.TabName>
            </S.Tab>
          </Link>
          <S.Separator />
        </>
      )}
      <Link
        href={getDashboardTabRoute(
          organizationId,
          projectId,
          selectedTab === Tabs.SETTINGS && previousCustomerId ? previousCustomerId : customerId,
          Tabs.FINANCIALDATA,
        )}
        scroll={false}
        passHref={true}
      >
        <S.Tab data-testid="financial-data-tab" selected={selectedTab === Tabs.FINANCIALDATA} expandOnHover>
          <S.FinancialDataIcon />
          <S.TooltipSeparator id="tabSeparator" />
          <S.TabName id="tabName">{intl.formatMessage(sidebarMessages.FINANCIALDATA)}</S.TabName>
        </S.Tab>
      </Link>
      <S.Separator />
      <Link
        href={getDashboardTabRoute(
          organizationId,
          projectId,
          selectedTab === Tabs.SETTINGS && previousCustomerId ? previousCustomerId : customerId,
          Tabs.IDENTITY,
        )}
        scroll={false}
        passHref={true}
      >
        <S.Tab data-testid="identity-tab" selected={selectedTab === Tabs.IDENTITY} expandOnHover>
          <S.IdentityIcon />
          <S.TooltipSeparator id="tabSeparator" />
          <S.TabName id="tabName">{intl.formatMessage(sidebarMessages.IDENTITY)}</S.TabName>
        </S.Tab>
      </Link>
      <S.Separator />
    </S.TopContainer>
  );
};

export const Sidebar: React.FC<{ selectedTab: string }> = ({ selectedTab }) => {
  const router = useRouter();
  const { customerId, currentTab } = useDashboardParams();
  const organizationId = useSelector(accessTokenOrganizationIdSelector) as string;
  const projectId = useSelector(projectIdSelector) as string;
  const intl = useIntl();

  useEffect(() => {
    const tabValues: string[] = Object.values(Tabs);
    const isTabValid = tabValues.includes(currentTab);
    if (!isTabValid && currentTab !== undefined) {
      // replace is used instead of push for redirecting in order not to "break" back browser feature
      router.push(getDashboardTabRoute(organizationId, projectId, customerId));
    }
  }, [organizationId, projectId, customerId, router, currentTab]);

  return (
    <S.Container>
      <S.TabContainer data-testid="sidebar-top-content">
        <TopContainer selectedTab={selectedTab} />
      </S.TabContainer>
      <S.BottomContainer data-testid="sidebar-bottom-content">
        <Link href="/settings" scroll={false} passHref={true} legacyBehavior>
          <a aria-label={intl.formatMessage(sidebarMessages.SETTINGS)}>
            <S.Tab selected={selectedTab === Tabs.SETTINGS} expandOnHover>
              <S.SettingsIconContainer>
                <SettingsIcon />
              </S.SettingsIconContainer>
              <S.TooltipSeparator id="tabSeparator" />
              <S.TabName data-testid="settings-page" id="tabName">
                {intl.formatMessage(sidebarMessages.SETTINGS)}
              </S.TabName>
            </S.Tab>
          </a>
        </Link>
      </S.BottomContainer>
    </S.Container>
  );
};

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: ${({ theme }) => theme.algoanBlue};
    box-sizing: border-box;
    min-height: 100%;
    max-height: 100%;
    padding: 0px 8px 14px 8px;
    position: relative;
    width: 56px;
    z-index: 10;

    a:link,
    a:visited {
      background-color: unset;
      text-decoration: none;
    }

    ${({ theme }) => theme.media.portrait`
      padding: 30px 4px 14px 4px;
      width: 48px;
    `}

    @media print {
      display: none;
    }
  `,
  TabContainer: styled.div`
    height: 500px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 30px;
    position: sticky;
    top: 60px;
  `,
  TopContainer: styled.div``,
  BottomContainer: styled.div`
    position: sticky;
    bottom: 14px;
  `,
  ReturnToHome: styled.div`
    background-color: ${({ theme }) => theme.secondary.softBlue4};
    border-bottom-right-radius: 25px;
    cursor: pointer;
    display: flex;
    height: 100px;
    margin: -30px -8px 32px -8px;
    position: relative;

    ${({ theme }) => theme.media.portrait`
      margin: -30px -4px 32px -4px;
    `}
  `,
  Separator: styled.div`
    border-bottom: 1px solid ${(props) => props.theme.secondary.softBlue3};
    margin: 32px 0;

    ${({ theme }) => theme.media.portrait`
      margin: 32px 4px;
    `}
  `,
  TooltipSeparator: styled.div`
    border-left: 1px solid rgb(240, 244, 247, 0.6);
    height: 25px;
    left: 40px;
    position: absolute;
    top: 8px;
    visibility: hidden;
  `,
  Tab: styled.div<{ selected?: boolean; expandOnHover?: boolean }>`
    background-color: ${(props) => (props.selected ? props.theme.main.deepBlue : 'unset')};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    height: 40px;
    justify-content: flex-end;
    position: relative;
    width: 40px;
    z-index: 1000;

    &:hover #tabName {
      visibility: visible;
    }

    &:hover #tabSeparator {
      visibility: visible;
    }

    &:hover {
      background-color: ${({ theme }) => theme.main.deepBlue};
      width: ${(props) => (props.expandOnHover ? 'fit-content' : '')};
    }
  `,
  TabName: styled.div`
    color: ${({ theme }) => theme.white};
    font-size: 14px;
    font-weight: 600;
    margin-left: 56px;
    margin-right: 16px;
    margin-top: 11px;
    visibility: hidden;
    white-space: nowrap;
    width: fit-content;
  `,
  HomeIcon: styled(HomeIcon)<{ padding?: string }>`
    left: 11px;
    position: absolute;
    top: 11px;
  `,
  HomeContainer: styled.div`
    display: flex;
    margin: auto 0;
    padding-left: 8px;

    ${({ theme }) => theme.media.portrait`
      padding-left: 4px;
    `}
  `,
  AnalysisIcon: styled(AnalysisIcon)`
    left: 10px;
    position: absolute;
    top: 10px;
  `,
  HistoryIconContainer: styled.div`
    left: 11px;
    position: absolute;
    top: 12px;
  `,
  StatementsIcon: styled(StatementsIcon)`
    left: 11px;
    position: absolute;
    top: 10px;
  `,
  LoanDecisionIcon: styled(LoanDecisionIcon)`
    left: 10px;
    position: absolute;
    top: 10px;
  `,
  DocumentsIcon: styled(DocumentsIcon)`
    left: 11px;
    position: absolute;
    top: 10px;
  `,
  FinancialDataIcon: styled(FinancialDataIcon)`
    left: 11px;
    position: absolute;
    top: 10px;
  `,
  IdentityIcon: styled(IdentityIcon)`
    left: 11px;
    position: absolute;
    top: 10px;
  `,
  SettingsIconContainer: styled.div`
    left: 11px;
    position: absolute;
    top: 11px;
  `,
};
